// The tab glyphs of the six game menus (R2214; ui-map-interface.md §12.0
// "The tab bar"): family 01 "The Dock Line" of mockup round 102, with the
// DRAFT glyph of round 103 card 02 and the FLEET glyph of card 07. The
// telephone tab bar and the landscape rail show the glyph alone, the open
// sheet names the menu in its head line, and the desktop dock shows the
// glyph and the word, measured, with a fall-back per tab (round 102 cards
// 14, 16 and 18). The setting TAB LABELS (R2215) gives a telephone the
// glyph over its short word where the bar has room.
//
// A glyph is a line icon in a 24-unit box with the stroke of the dock
// icons (R1740): `currentColor`, 1.7 wide, round caps and joins. FLEET is
// the one solid glyph: three map darts, filled, with a 0.8 rim. The table
// is keyed by the tab key, never by a display word, and it names every
// game tab, so `tsc` refuses a new tab with no glyph.
//
// This module is pure markup and arithmetic. The overlay owns the bar, the
// measurement in the page and the presses.

import { runeCount } from '../util/text';
import { esc } from './esc';
import { TAB_GLYPH } from '../gen/presentation';
import { dartPath, type MarkGraphics } from '../render/marks';
import { GAME_TABS, tabLabel } from './overlay/tables';

/** One of the six game menus. */
export type GameTab = typeof GAME_TABS[number];

/** Two decimals, as the mockup pages write a figure. */
const f2 = (n: number): number => +n.toFixed(2);

/** The points of the map dart of `render/marks.ts`, read once from its
 *  path: nose, right tail, notch, left tail, with the nose ahead on +x. */
function dartPoints(): number[] {
  let pts: number[] = [];
  const rec = { poly(p: number[]) { pts = p; return rec; } };
  dartPath(rec as unknown as MarkGraphics);
  return pts;
}

/**
 * One map dart, nose up, at the ratio of `dartPath` (round 103 card 07):
 * the nose at (`cx`, `ny`) and `len` units from the nose to the tail. The
 * map dart is 22 px long, 5 px half-wide at the tail and notched 19 px
 * behind the nose, so the half width is 5/22 of the length and the notch
 * stands at 19/22.
 */
export function dartGlyphPath(cx: number, ny: number, len: number): string {
  const [nose, , tailX, tailHalf, notch] = dartPoints();
  const long = nose - tailX;
  const w = (tailHalf / long) * len, t = ny + len, n = ny + ((nose - notch) / long) * len;
  return `M${f2(cx)} ${f2(ny)}L${f2(cx + w)} ${f2(t)}L${f2(cx)} ${f2(n)}L${f2(cx - w)} ${f2(t)}Z`;
}

/** The root attributes of a line glyph: the stroke of the dock icons. */
const LINE = 'fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"';
/** The root attributes of the solid FLEET glyph (round 103 card 07). */
const SOLID = 'fill="currentColor" stroke="currentColor" stroke-width=".8" stroke-linejoin="round"';

/** A torpedo, nose up (round 102 set 01): a slim hull with a round nose. */
const TORP = 'M10.6 17V4.6a1.4 2 0 0 1 2.8 0V17z';
/** The two blades of its screw. */
const SCREW = 'M10.6 17l-1.6 3M13.4 17l1.6 3';

/**
 * The crossed torpedoes of WAR. The page of round 102 cut the crossing
 * with an SVG mask, and a mask needs an id that is unique in the
 * document; a bar, a rail and a sheet head draw many copies. So the
 * torpedo underneath is drawn as the parts the mask left: the body from
 * its nose to the edge of the gap, and the outer end of its left screw
 * blade. Each cut end stops half a stroke short of the gap, so its round
 * cap ends where the mask cut. The gap is the body of the upper torpedo
 * stroked 5.2 wide, as on the page.
 */
const WAR = `<g transform="rotate(36 12 14.6)"><path d="M10.6 9.09V4.6a1.4 2 0 0 1 2.8 0V10M9.59 18.9L9 20"/></g>`
  + `<g transform="rotate(-36 12 14.6)"><path d="${TORP}"/><path d="${SCREW}"/></g>`;

/** One glyph: its root attributes and its inside in the 24-unit box. */
interface Glyph { root: string; d: string }

/** The six glyphs, by tab key. */
const GLYPHS: Readonly<Record<GameTab, Glyph>> = {
  // A hammer. Not a crane: the crane is the Works Yard row in BUILD (R1855).
  BUILD: { root: LINE, d: '<path d="M10.4 6.5l3.1-3.1 7.1 7.1-3.1 3.1z"/><path d="M13.9 10.1L4.4 19.6"/>' },
  // The rolled print (round 103 card 02): the roll at the left edge, the
  // sheet unrolled to the right, and a small filled hull on it.
  DRAFT: {
    root: LINE,
    d: '<path d="M5.1 3.5a2.1 2.1 0 0 1 2.1 2.1v12.8a2.1 2.1 0 0 1-4.2 0V5.6a2.1 2.1 0 0 1 2.1-2.1z"/>'
      + '<path d="M7.2 5.2h13.3v13.6H7.2"/>'
      + '<path d="M9.9 12h9.6l-1.6 2.6H11.5z" fill="currentColor" stroke-width="1"/>'
      + '<path d="M13.3 12V9.8h2.6l.8 2.2" stroke-width="1.3"/>',
  },
  // The map vee (round 103 card 07): three map darts, solid.
  FLEET: {
    root: SOLID,
    d: `<path d="${dartGlyphPath(12, 1.4, 13)}"/><path d="${dartGlyphPath(5.4, 9.3, 13)}"/>`
      + `<path d="${dartGlyphPath(18.6, 9.3, 13)}"/>`,
  },
  // A peaked cap: the crown, the band, the visor and the badge.
  CAPTAIN: {
    root: LINE,
    d: '<path d="M3.6 10.8C3.6 7.4 7.4 5 12 5s8.4 2.4 8.4 5.8z"/><path d="M6.3 10.8v3.6h11.4v-3.6"/>'
      + '<path d="M6.3 14.4c1.4 2.6 3.4 3.9 5.7 3.9s4.3-1.3 5.7-3.9"/>'
      + '<circle cx="12" cy="8.1" r="1.2" fill="currentColor" stroke="none"/>',
  },
  // A balance: the beam, the post and two pans.
  ECONOMY: {
    root: LINE,
    d: '<circle cx="12" cy="4.2" r="1.3"/>'
      + '<path d="M12 5.5v14M8 19.5h8M4.5 7.5h15M4.5 7.5l-2 5.6M4.5 7.5l2 5.6M19.5 7.5l-2 5.6M19.5 7.5l2 5.6"/>'
      + '<path d="M2.2 13.1h4.6a2.3 2.3 0 0 1-4.6 0zM17.2 13.1h4.6a2.3 2.3 0 0 1-4.6 0z"/>',
  },
  // Two crossed torpedoes.
  WAR: { root: LINE, d: WAR },
};

/** True when `tab` is one of the six game menus. */
export function isGameTab(tab: string): tab is GameTab {
  return (GAME_TABS as readonly string[]).includes(tab);
}

/** The glyph of one game menu, `px` square, hidden from a screen reader:
 *  the tab keeps its word as its name. */
export function tabGlyph(tab: GameTab, px: number = TAB_GLYPH.deskPx): string {
  const g = GLYPHS[tab];
  return `<svg class="tgl" viewBox="0 0 24 24" width="${px}" height="${px}" ${g.root}`
    + ` aria-hidden="true" focusable="false">${g.d}</svg>`;
}

/**
 * The inside of one tab: the glyph, then the word the plan gave it. The
 * word stands in the markup under every form, so the bar keeps one shape
 * of markup; the class `g` of a tab of form G hides it. The tab
 * itself carries its full word as its name and its title.
 */
export function tabInnerHtml(tab: GameTab, word: string): string {
  return tabGlyph(tab) + `<span class="tw" aria-hidden="true">${esc(word)}</span>`;
}

/**
 * The head line of an open game menu (round 102 card 16): the glyph at
 * `head_px` and the full menu word. It stands at the top of the menu, over
 * its pills, on the telephone sheet, beside the rail and in the desktop
 * dock body.
 */
export function sheetHeadHtml(tab: GameTab): string {
  return `<div class="tabhead">${tabGlyph(tab, TAB_GLYPH.headPx)}<span>${esc(tabLabel(tab))}</span></div>`;
}

// --- the fit -----------------------------------------------------------------

/** How a tab shows its name: glyph and full word (F), glyph and short
 *  word (S), or the glyph alone (G). */
export type TabForm = 'F' | 'S' | 'G';

/** The width of a word in px of chrome, measured with the real face. */
export type Measure = (word: string) => number;

/** The two words of one tab. */
export interface TabWords { full: string; short: string }

/** The one canvas the words are measured on; null where the page has
 *  none (a test). */
let measureCtx: CanvasRenderingContext2D | null | undefined;

/**
 * A measure of a word in the face, the size and the tracking of `cs`, the
 * computed style of a tab, with canvas `measureText` (round 102 page
 * `ww`). CSS tracking stands after every letter, so a word of n letters
 * takes n times the tracking. Null when the page can draw on no canvas.
 */
export function wordMeasure(cs: CSSStyleDeclaration): Measure | null {
  if (measureCtx === undefined) {
    try { measureCtx = document.createElement('canvas').getContext('2d'); } catch { measureCtx = null; }
  }
  const ctx = measureCtx;
  if (!ctx) return null;
  const track = parseFloat(cs.letterSpacing) || 0;
  const font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  return (word) => {
    ctx.font = font;
    return ctx.measureText(word).width + runeCount(word) * track;
  };
}

/** The fixed lengths of a plan, in px of chrome. */
export interface TabFit {
  /** The room for the six tabs: the bar less its padding and its gaps,
   *  and on the telephone also less the two caps with their margins. */
  room: number;
  /** The rim and the side padding of one tab. */
  frame: number;
}

/**
 * The plan of the desktop dock (round 102 card 14): each tab shows its
 * glyph and its full word; while the six do not fit the room, the tab
 * with the longest need falls back to its short word, then to the glyph
 * alone, one tab at a time. The words come from the locale and `measure`
 * reads them in the real face, so a new language needs no CSS rule.
 */
export function deskPlan(words: readonly TabWords[], fit: TabFit, measure: Measure): TabForm[] {
  const fw = words.map((w) => measure(w.full)), sw = words.map((w) => measure(w.short));
  const withWord = fit.frame + TAB_GLYPH.deskPx + TAB_GLYPH.gapPx;
  const need = (f: TabForm, i: number): number => f === 'G'
    ? fit.frame + TAB_GLYPH.deskAlonePx : withWord + (f === 'F' ? fw[i] : sw[i]);
  const forms: TabForm[] = words.map(() => 'F');
  const total = (): number => forms.reduce((a, f, i) => a + need(f, i), 0);
  // Each step takes one tab one form down, so the walk ends.
  for (let guard = 2 * words.length; guard > 0 && total() > fit.room; guard--) {
    let bi = -1, bn = -1;
    forms.forEach((f, i) => { if (f !== 'G' && need(f, i) > bn) { bn = need(f, i); bi = i; } });
    if (bi < 0) break;
    forms[bi] = forms[bi] === 'F' && sw[bi] < fw[bi] ? 'S' : 'G';
  }
  return forms;
}

/**
 * The plan of the telephone tab bar under GLYPH + WORD (round 102 card 17,
 * page `phoneBar` mode `under` with `fallback`): each tab shows its glyph
 * over its short word; while the bar does not fit, the tab with the
 * longest word falls back to the glyph alone, one tab at a time. A tab is
 * as wide as the wider of its glyph and its word.
 */
export function phonePlan(words: readonly string[], fit: TabFit, measure: Measure): TabForm[] {
  const w = words.map((x) => measure(x));
  const glyph = TAB_GLYPH.wordPx;
  const forms: TabForm[] = words.map(() => 'S');
  const total = (): number => forms.reduce((a, f, i) => a + fit.frame + (f === 'S' ? Math.max(glyph, w[i]) : glyph), 0);
  for (let guard = words.length; guard > 0 && total() > fit.room; guard--) {
    let bi = -1, bw = -1;
    forms.forEach((f, i) => { if (f === 'S' && w[i] > bw) { bw = w[i]; bi = i; } });
    if (bi < 0) break;
    forms[bi] = 'G';
  }
  return forms;
}
